import { call } from '@rental/contracts/client'
import { format } from '@rental/i18n'
import { Notice } from '@rental/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { ChoiceChips } from '../components/ChoiceChips'
import { StatusChip } from '../components/StatusChip'
import { Loading, ProblemNotice } from '../components/ui'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import { drawerState, rememberedPoint, rememberPoint } from './drawer'
import { OpenDrawerForm } from './OpenDrawer'
import { CASH_QUERY_KEY, loadCash } from './queries'

/**
 * S5 (PAY-6, Fady 15 Oct): the drawer cash goes into or out of, inside a
 * money form. The device remembers its pick-up point; the field says whether
 * that drawer is open, and opens it with a count when it is not. The form
 * sends the open drawer's id (`onChange`), or nothing while there is none,
 * and the server checks it again.
 */
export function DrawerField({
  name,
  code,
  onChange,
}: {
  /** Unique on the page: the radio group's name. */
  name: string
  /**
   * S9: the booking the money is for. Someone working a trip given to them,
   * whose role keeps no drawers, puts cash into one of the drawers open now.
   */
  code?: string
  onChange: (sessionId: string | null) => void
}) {
  const { access } = useAccess()
  if (can(access, 'payments.record')) return <CounterDrawerField name={name} onChange={onChange} />
  if (code) return <TripDrawerField name={name} code={code} onChange={onChange} />
  return <CounterDrawerField name={name} onChange={onChange} />
}

/** At the counter: the device's own point, opened here with a count when it is not. */
function CounterDrawerField({
  name,
  onChange,
}: {
  name: string
  onChange: (sessionId: string | null) => void
}) {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const allowed = can(access, 'payments.record')
  // Read afresh whenever the field shows: another device may have closed the drawer.
  const cash = useQuery({
    queryKey: CASH_QUERY_KEY,
    queryFn: loadCash,
    enabled: allowed,
    staleTime: 0,
  })
  const [chosen, setChosen] = useState<string | null>(rememberedPoint)
  const state = cash.data ? drawerState(cash.data, chosen) : null
  const sessionId = state?.kind === 'open' ? state.sessionId : null
  const problem = problemOf(cash.error)
  // Opened from this field: once it reads as open, its line takes the focus (UI-2g). The
  // form that opened it goes, and the focus with it, onto nothing.
  const openedHere = useRef(false)
  const openLine = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    onChange(sessionId)
  }, [sessionId, onChange])

  useEffect(() => {
    if (sessionId === null || !openedHere.current) return
    openedHere.current = false
    openLine.current?.focus()
  }, [sessionId])

  function choose(locationId: string) {
    setChosen(locationId)
    rememberPoint(locationId)
  }

  return (
    // In a well, as every money form is: its legend groups it, with no box drawn round it (UI-2g).
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1 text-sm font-medium">{m.cash.field.legend}</legend>
      {!allowed ? (
        <p className="text-sm text-text-muted">{m.cash.field.noAccess}</p>
      ) : !cash.data ? (
        problem ? (
          <ProblemNotice problem={problem} />
        ) : (
          <Loading />
        )
      ) : (
        <>
          {cash.data.drawers.length > 1 ? (
            <ChoiceChips
              name={name}
              label={m.cash.field.point}
              labelHidden
              options={cash.data.drawers.map(({ location }) => ({
                value: location.id,
                label: location.name,
              }))}
              value={
                state && state.kind !== 'none' && state.kind !== 'choose'
                  ? state.drawer.location.id
                  : null
              }
              onChange={choose}
            />
          ) : null}
          {state?.kind === 'none' ? <p>{m.cash.field.noPoint}</p> : null}
          {state?.kind === 'choose' ? (
            <p className="text-sm text-text-muted">{m.cash.field.choose}</p>
          ) : null}
          {state?.kind === 'open' ? (
            <p
              ref={openLine}
              tabIndex={-1}
              className="flex flex-wrap items-center gap-2 focus:outline-none"
            >
              <StatusChip tone="success">{m.cash.state.open}</StatusChip>
              {format(m.cash.field.isOpen, { point: state.drawer.location.name })}
            </p>
          ) : null}
          {state?.kind === 'counting' ? (
            <Notice tone="warning">
              <p>
                {format(m.cash.field.isCounting, { point: state.drawer.location.name })}{' '}
                <Link to="/cash" className="touch-target">
                  {m.cash.field.toCash}
                </Link>
              </p>
            </Notice>
          ) : null}
          {state?.kind === 'closed' ? (
            <>
              <p>{format(m.cash.field.isClosed, { point: state.drawer.location.name })}</p>
              <OpenDrawerForm
                drawer={state.drawer}
                onOpened={() => {
                  openedHere.current = true
                }}
              />
            </>
          ) : null}
        </>
      )}
    </fieldset>
  )
}

/**
 * S9 (Q-62): cash taken on a trip, still in the pocket of whoever drove it,
 * goes into one of the drawers open now, the one of the point that reports
 * the trip's zone first. Opening a drawer is the counter's: none open, no
 * cash. The blind count at the drawer stays the control.
 */
function TripDrawerField({
  name,
  code,
  onChange,
}: {
  name: string
  code: string
  onChange: (sessionId: string | null) => void
}) {
  const { messages: m } = useLocale()
  // Under the drawers' key: a refusal for a drawer closed meanwhile reads these again too.
  const drawers = useQuery({
    queryKey: [...CASH_QUERY_KEY, 'booking', code.toUpperCase()],
    queryFn: () =>
      load(call(api.GET('/v1/staff/bookings/{code}/drawers', { params: { path: { code } } }))),
    staleTime: 0,
  })
  const [chosen, setChosen] = useState<string | null>(null)
  const items = drawers.data?.items ?? []
  const [only] = items
  // Chosen and still open; else the reporting point's; else the one drawer open.
  const sessionId =
    (items.some((item) => item.id === chosen) ? chosen : null) ??
    drawers.data?.suggestedId ??
    (items.length === 1 && only ? only.id : null)
  const into = items.find((item) => item.id === sessionId)
  const problem = problemOf(drawers.error)

  useEffect(() => {
    onChange(sessionId)
  }, [sessionId, onChange])

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1 text-sm font-medium">{m.cash.field.legend}</legend>
      {!drawers.data ? (
        problem ? (
          <ProblemNotice problem={problem} />
        ) : (
          <Loading />
        )
      ) : items.length === 0 ? (
        <p>{m.cash.field.tripNone}</p>
      ) : (
        <>
          {items.length > 1 ? (
            <ChoiceChips
              name={name}
              label={m.cash.field.point}
              labelHidden
              options={items.map((item) => ({ value: item.id, label: item.location.name }))}
              value={sessionId}
              onChange={setChosen}
            />
          ) : null}
          {into ? (
            <p className="flex flex-wrap items-center gap-2">
              <StatusChip tone="success">{m.cash.state.open}</StatusChip>
              {format(m.cash.field.tripInto, { point: into.location.name })}
            </p>
          ) : (
            <p className="text-sm text-text-muted">{m.cash.field.tripChoose}</p>
          )}
        </>
      )}
    </fieldset>
  )
}
